import { useEffect, useRef, useState } from 'react';

import { isClientAttachment } from '@entities/attachment/lib';
import {
    TAttachmentDTO,
    TAttachmentDTOCamelCased,
    TAttachmentId,
    TAttachmentOnClient,
} from '@entities/attachment/model/types';
import { downloadFile, getNameExtension } from '@shared/helpers';

interface IUseAttachmentsListProps {
    onDeleteAttachmentCb: (attachmentId: TAttachmentId) => void;
}

export const useAttachmentsList = ({ onDeleteAttachmentCb }: IUseAttachmentsListProps) => {
    const anchorRefs = useRef<Record<string, HTMLElement | null>>({});
    const [openedMenuId, setOpenedMenuId] = useState<TAttachmentId>(null);
    const [itemInActionId, setItemInActionId] = useState(null);
    const [isOpenDeletePopup, setIsOpenDeletePopup] = useState(false);

    const handleOpenDeletePopup = (deletedId: TAttachmentId) => {
        setIsOpenDeletePopup(true);
        setItemInActionId(deletedId);
    };
    const onHidePopup = (): void => {
        setIsOpenDeletePopup(false);
    };

    const toggleContextMenu = (fileId: TAttachmentId) => {
        setOpenedMenuId((prev) => (prev === fileId ? null : fileId));
    };

    const onDeleteAttachment = () => {
        onDeleteAttachmentCb(itemInActionId);
        setItemInActionId(null);
        onHidePopup();
    };
    const onDownloadAttachment = async (file: TAttachmentOnClient | TAttachmentDTOCamelCased) => {
        const isFromClient = isClientAttachment(file);
        await downloadFile(isFromClient ? file?.file : (file as unknown as File));
    };

    const resetOpenMenuId = () => {
        setOpenedMenuId(null);
    };

    const getAttachmentInfo = (file: TAttachmentOnClient | TAttachmentDTOCamelCased) => {
        const isFromClient = isClientAttachment(file);
        const isFileLoading = isFromClient ? file?.isLoading : false;
        const isImg = isFromClient
            ? file?.file?.type.startsWith('image/')
            : file?.mimeType?.startsWith('image/');
        const fileImgUrl = isFromClient ? URL.createObjectURL(file?.file) : file?.previewUrl;
        const fileName = isFromClient ? file?.file?.name : file?.name;
        const { extension: fileExtension, name } = getNameExtension(fileName);

        return {
            isFileLoading,
            isImg,
            fileImgUrl,
            fileName: name,
            fileExtension,
        };
    };

    return {
        anchorRefs,
        isOpenDeletePopup,
        openedMenuId,
        handleOpenDeletePopup,
        toggleContextMenu,
        onDeleteAttachment,
        onDownloadAttachment,
        onHidePopup,
        resetOpenMenuId,
        getAttachmentInfo,
    };
};
